import { bind } from 'app/utils/decorators/decoratorUtils';
import EntityLayer from 'app/containers/Maps/EntityLayerStyling/EntityLayer';
import { loadEntityDetails } from 'store/actions/maps/situationalAwarenessActions';
import cesiumLayerConfig from 'app/config/cesiumLayerConfig';

class ThreeDLayer extends EntityLayer {
    cesiumAssets = {
        terrainProvider: null,
        tileset: null,
        imageryLayer: null,
    };

    constructor(info, map) {
        super(info, map);
    }
    
    safeSceneOperation(action, errorMessage) {
        try {
            action();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(errorMessage, error);
        }
    }
    async toggleTo3DStyling(Cesium, ol3D, enable3D) {
        const assetId = cesiumLayerConfig?.cesiumLayer?.getAsset(this.info) || cesiumLayerConfig?.cesiumLayer?.getOldAsset(this.info);
        const entityId = cesiumLayerConfig?.cesiumLayer?.getAccount(this.info)?.id || cesiumLayerConfig?.cesiumLayer?.getOldAccount(this.info)?.id;
        const oldType = cesiumLayerConfig?.cesiumAccount?.oldType;
        const type = this.info?.type === cesiumLayerConfig?.cesiumLayer?.oldType ? oldType : cesiumLayerConfig?.cesiumAccount?.type;
        
        const result = await loadEntityDetails(entityId, type);
        const token = cesiumLayerConfig?.cesiumAccount?.getCesiumToken(result) || cesiumLayerConfig?.cesiumAccount?.getOldCesiumToken(result);
        
        await this.addCesiumAsset(token, assetId, Cesium, ol3D);
    }

    async addCesiumAsset(token, assetId, Cesium, ol3D) {
        try {
            this.cesiumToken = token;
            this.assetId = assetId;
            this.CesiumLib = Cesium;
            this._ol3d = ol3D;
            
            Cesium.Ion.defaultAccessToken = token;
            
            const scene = ol3D.getCesiumScene();
            const assetType = assetId?.split(',')[1];
            const twoLayersVisibility = this.layer?.getVisible();
            
            this._ol3d = ol3D;
         
            switch (assetType) {
                case '3DTILES':
                    const assetIdNumber = Number(assetId?.split(',')[0]);
                    const resource = await Cesium.IonResource.fromAssetId(assetIdNumber);
                    const tileset = await Cesium.Cesium3DTileset.fromUrl(resource, {
                        projectTo2D: true,
                    });

                    scene.primitives.add(tileset);
                    tileset.featureIdLabel = this.info.id;
                    tileset.show = !!twoLayersVisibility;
                    this.cesiumAssets.tileset = tileset;
                    this.layer.setProperties({ oltileset: tileset });
                    break;
                case 'IMAGERY':
                    const imageryLayers = this._ol3d?.getCesiumScene()?.globe?.imageryLayers;   
                    const imageryProvider = new Cesium.IonImageryProvider({ assetId: assetId?.split(',')[0] });
                    const imageryLayer = imageryLayers?.addImageryProvider(imageryProvider);
                    
                    if (imageryLayer) {
                        imageryLayer.featureIdLabel = this.info.id;
                        imageryLayer.show = !!twoLayersVisibility;
                        this.cesiumAssets.imageryLayer = imageryLayer;
                    } 
                    break;
                case 'TERRAIN':
                    const ionTerrain = new Cesium.CesiumTerrainProvider({ url: Cesium.IonResource.fromAssetId(assetId?.split(',')[0]) });
                    this._ol3d.getCesiumScene().globe.terrainProvider(ionTerrain);
                    this.cesiumAssets.terrainProvider = ionTerrain;
                    
                    if (ionTerrain.readyPromise) {
                        ionTerrain.readyPromise.then(() => {
                        }).catch((error) => {
                            console.error('[ThreeDLayer.addCesiumAsset] Terrain provider failed to load:', error);
                        });
                    }
                    break;
                default:
                    return;
            }
            
            if (scene?.requestRender) {
                scene.requestRender();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    }

    async apply3DVisibility(visible) {
        try {
            const v = !!visible;

            this.layer?.setProperties?.({ visible3d: v });
            this.layer?.setVisible?.(v);
            
            const hasAssets = this.cesiumAssets.tileset || this.cesiumAssets.imageryLayer || this.cesiumAssets.terrainProvider;
           
            if (!hasAssets && this.assetId && this.cesiumToken && this.CesiumLib && this._ol3d) {
                await this.addCesiumAsset(this.cesiumToken, this.assetId, this.CesiumLib, this._ol3d);
                return;
            }
            
            if (this.cesiumAssets.tileset) {
                this.cesiumAssets.tileset.show = v;
            }
            if (this.cesiumAssets.imageryLayer) {
                this.cesiumAssets.imageryLayer.show = v;
            }
         
            
            const scene = this._ol3d?.getCesiumScene?.();
            if (scene?.requestRender) {
                scene.requestRender();
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[ThreeDLayer.apply3DVisibility] Error:', err);
        }
    }

    async updateCesiumVisibility(visible) {
        await this.apply3DVisibility(visible);
    }

    async sync3DVisibility(visible) {
        super.sync3DVisibility(visible);
        await this.apply3DVisibility(visible);
    }

    cleanupCesiumAssets() {
        try {
            if (!this._ol3d) {
                return;
            }

            const scene = this._ol3d.getCesiumScene();
            if (!scene) {
                return;
            }

            if (this.cesiumAssets.tileset) {
                this.safeSceneOperation(
                    () => { scene.primitives.remove(this.cesiumAssets.tileset); },
                    '[ThreeDLayer] Error removing tileset:'
                );
                this.cesiumAssets.tileset = null;
            }

            if (this.cesiumAssets.imageryLayer) {
                this.safeSceneOperation(
                    () => {
                        const imageryLayers = scene.globe?.imageryLayers;
                        if (imageryLayers) {
                            imageryLayers.remove(this.cesiumAssets.imageryLayer);
                        }
                    },
                    '[ThreeDLayer] Error removing imagery layer:'
                );
                this.cesiumAssets.imageryLayer = null;
            }

            if (this.cesiumAssets.terrainProvider) {
                this.safeSceneOperation(
                    () => { scene.globe.terrainProvider = new this._ol3d.getCesium().EllipsoidTerrainProvider(); },
                    '[ThreeDLayer.cleanupCesiumAssets] Error resetting terrain:'
                );
                this.cesiumAssets.terrainProvider = null;
            }

            if (scene.requestRender) {
                scene.requestRender();
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('[ThreeDLayer] Error during Cesium assets cleanup:', error);
        }
    }

    @bind
    async loader(extent, resolution, projection) {
        // Skip the parent loader which calls cleanup()
        return Promise.resolve();
    }

    @bind
    cleanup() {
        this.cleanupCesiumAssets();
        if (this._loaderAnimationFrames) {
            this._loaderAnimationFrames.forEach(frameId => {
                cancelAnimationFrame(frameId);
            });
            this._loaderAnimationFrames = [];
        }
    }
}

export default ThreeDLayer;
